import {
  Queue,
  QueueItem,
  QueueItemAction,
  QueueItemActions,
  QueueItemContent,
  QueueList,
  QueueSection,
  QueueSectionContent,
  QueueSectionLabel,
  QueueSectionTrigger,
} from '@crowdex/ui/ai/queue';
import { Button } from '@crowdex/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import {
  ArrowDown,
  ArrowUp,
  CornerDownLeft,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import type { FollowUpQueuePause, QueuedFollowUp } from '@/lib/desktop-shell/store';
import { useOnline } from '@/lib/online-status';

/** The queue's pause line: the real reason it stopped draining. */
const QUEUE_PAUSE_REASONS: Readonly<Record<FollowUpQueuePause, string>> = {
  interrupted: 'Paused because you interrupted',
  'cloud-start-failed': "Paused: the cloud sandbox couldn't start",
  'send-failed': "Paused: the message couldn't be sent",
};

/**
 * Draggable list of follow-up messages queued for the active conversation,
 * mirroring Cursor's composer queue: a "N Queued Message(s)" header with a
 * clear-all control, and per-item actions (Send Now, edit, delete, and a more
 * menu for reordering). Purely presentational — `DesktopShell`/the composer own
 * the queue state and supply the callbacks. Renders nothing when the queue is
 * empty.
 */
export function QueuedFollowUpList({
  busy,
  messages,
  paused,
  onClear,
  onDelete,
  onEdit,
  onReorder,
  onResume,
  onSendNow,
}: {
  busy: boolean;
  messages: readonly QueuedFollowUp[];
  /** Why the queue stopped draining; `undefined` while it drains. */
  paused: FollowUpQueuePause | undefined;
  onClear(): void;
  onDelete(id: string): void;
  onEdit(item: QueuedFollowUp): void;
  onReorder(draggedId: string, targetId: string): void;
  onResume(): void;
  onSendNow(item: QueuedFollowUp): void;
}) {
  const online = useOnline();
  if (messages.length === 0) return null;
  // Offline, what was sent waits as the message it will be (design D53).
  if (!online)
    return (
      <ol
        aria-label="Messages waiting for the network"
        className="mx-auto mb-4 grid w-full max-w-conversation justify-items-end gap-2"
      >
        {messages.map((queued) => (
          <li
            className="group relative max-w-[80%] rounded-2xl border border-hairline-strong border-dashed px-3.5 py-2"
            data-offline-queued
            key={queued.id}
          >
            <p className="whitespace-pre-wrap text-foreground text-sm">
              {queued.message.text ||
                `${queued.message.files.length} attachment${queued.message.files.length === 1 ? '' : 's'}`}
            </p>
            <p className="type-meta text-muted-foreground">Will send when you're back online</p>
            <QueueItemAction
              aria-label="Remove from queue"
              className="absolute top-1 -left-8 size-7 p-0 can-hover:opacity-0 group-hover:opacity-100"
              onClick={() => onDelete(queued.id)}
              title="Remove from queue"
            >
              <Trash2 className="size-3.5" />
            </QueueItemAction>
          </li>
        ))}
      </ol>
    );
  return (
    <Queue className="mx-auto mb-4 w-full max-w-conversation border-hairline-strong bg-card shadow-sm">
      <QueueSection>
        <div className="flex items-center gap-1">
          <QueueSectionTrigger className="flex-1 bg-transparent px-1 hover:bg-muted/40">
            <QueueSectionLabel
              count={messages.length}
              label={messages.length === 1 ? 'Queued Message' : 'Queued Messages'}
            />
          </QueueSectionTrigger>
          <QueueItemAction
            aria-label="Clear queued messages"
            className="opacity-100"
            onClick={onClear}
            title="Clear queued messages"
          >
            <X className="size-3.5" />
          </QueueItemAction>
        </div>
        <QueueSectionContent>
          {paused !== undefined && (
            <div className="mb-2 flex items-center justify-between px-3 text-muted-foreground text-xs">
              <span>{QUEUE_PAUSE_REASONS[paused]}</span>
              <button
                className="font-medium text-foreground hover:underline"
                onClick={onResume}
                type="button"
              >
                {paused === 'interrupted' ? 'Resume' : 'Retry'}
              </button>
            </div>
          )}
          <QueueList className="max-h-[30dvh] space-y-2">
            {messages.map((queued, index) => {
              const previousId = index > 0 ? messages[index - 1]?.id : undefined;
              const nextId = index < messages.length - 1 ? messages[index + 1]?.id : undefined;
              return (
                <QueueItem
                  className="group flex min-h-14 flex-row items-center gap-2 rounded-2xl border border-hairline-strong bg-card px-3 py-2.5 shadow-sm"
                  draggable
                  key={queued.id}
                  onDragOver={(event) => event.preventDefault()}
                  onDragStart={(event) =>
                    event.dataTransfer.setData('text/crowdex-follow-up', queued.id)
                  }
                  onDrop={(event) => {
                    event.preventDefault();
                    const draggedId = event.dataTransfer.getData('text/crowdex-follow-up');
                    if (draggedId.length > 0) onReorder(draggedId, queued.id);
                  }}
                >
                  <GripVertical className="size-3.5 shrink-0 cursor-grab text-muted-foreground/50 can-hover:opacity-0 transition-opacity group-hover:opacity-100" />
                  <QueueItemContent className="min-w-0 flex-1 text-foreground">
                    {queued.message.text ||
                      `${queued.message.files.length} attachment${
                        queued.message.files.length === 1 ? '' : 's'
                      }`}
                  </QueueItemContent>
                  <QueueItemActions className="shrink-0 items-center gap-0.5 opacity-80 transition-opacity group-hover:opacity-100">
                    <Button
                      className="h-control-compact gap-1 px-2"
                      onClick={() => onSendNow(queued)}
                      size="sm"
                      title={busy ? 'Send without interrupting the agent' : 'Send this message now'}
                      variant="ghost"
                    >
                      Send Now
                      <CornerDownLeft className="size-3" />
                    </Button>
                    <QueueItemAction
                      aria-label="Edit queued message"
                      className="size-7 p-0"
                      onClick={() => onEdit(queued)}
                      title="Edit queued message"
                    >
                      <Pencil className="size-3.5" />
                    </QueueItemAction>
                    <QueueItemAction
                      aria-label="Remove from queue"
                      className="size-7 p-0"
                      onClick={() => onDelete(queued.id)}
                      title="Remove from queue"
                    >
                      <Trash2 className="size-3.5" />
                    </QueueItemAction>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          aria-label="More queued message actions"
                          className="size-control-compact p-0"
                          size="sm"
                          variant="ghost"
                        >
                          <MoreHorizontal className="size-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          disabled={previousId === undefined}
                          onSelect={() => {
                            if (previousId !== undefined) onReorder(queued.id, previousId);
                          }}
                        >
                          <ArrowUp className="size-3.5" />
                          Move up
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={nextId === undefined}
                          onSelect={() => {
                            if (nextId !== undefined) onReorder(queued.id, nextId);
                          }}
                        >
                          <ArrowDown className="size-3.5" />
                          Move down
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </QueueItemActions>
                </QueueItem>
              );
            })}
          </QueueList>
        </QueueSectionContent>
      </QueueSection>
    </Queue>
  );
}
